import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactFlow } from 'reactflow';
import { logger } from '../utils/logger';
import { X, Plus, Trash2, Save, Key, Code, Tag, ChevronUp, ChevronDown, ChevronRight, GripVertical, Copy, ArrowRight, ArrowLeft, Link2, Database, Library, Hash, FileText, Maximize2, Filter, Lock, Unlock } from 'lucide-react';
import useFlowStore from '../stores/useFlowStore';
import { DATA_TYPES, CARDINALITY, NODE_TYPES } from '../utils/constants';
import { createDefaultField, isNodeMatchingFilter } from '../utils/helpers';
import { useConfirm } from '../hooks/useConfirm';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from './modals/ConfirmDialog';
import DuplicateEntityModal from './modals/DuplicateEntityModal';
import SaveDomainModal from './modals/SaveDomainModal';
import ManageIndexesModal from './modals/ManageIndexesModal';
import BusinessRulesModal from './modals/BusinessRulesModal';
import EntityEditModal from './modals/EntityEditModal';
import { domainApi } from '../utils/api';
import toast from 'react-hot-toast';

const TAG_COLORS = [
  { name: 'Red', value: '#EF4444', bg: '#FEE2E2' },
  { name: 'Orange', value: '#F97316', bg: '#FFEDD5' },
  { name: 'Amber', value: '#F59E0B', bg: '#FEF3C7' },
  { name: 'Yellow', value: '#EAB308', bg: '#FEF9C3' },
  { name: 'Lime', value: '#84CC16', bg: '#ECFCCB' },
  { name: 'Green', value: '#10B981', bg: '#D1FAE5' },
  { name: 'Emerald', value: '#059669', bg: '#D1FAE5' },
  { name: 'Teal', value: '#14B8A6', bg: '#CCFBF1' },
  { name: 'Cyan', value: '#06B6D4', bg: '#CFFAFE' },
  { name: 'Sky', value: '#0EA5E9', bg: '#E0F2FE' },
  { name: 'Blue', value: '#3B82F6', bg: '#DBEAFE' },
  { name: 'Indigo', value: '#6366F1', bg: '#E0E7FF' },
  { name: 'Violet', value: '#8B5CF6', bg: '#EDE9FE' },
  { name: 'Purple', value: '#A855F7', bg: '#F3E8FF' },
  { name: 'Fuchsia', value: '#D946EF', bg: '#FAE8FF' },
  { name: 'Pink', value: '#EC4899', bg: '#FCE7F3' },
  { name: 'Rose', value: '#F43F5E', bg: '#FFE4E6' },
  { name: 'Gray', value: '#6B7280', bg: '#F3F4F6' },
];

const Properties = ({ isOpen, onClose }) => {
  const { userHasRole } = useAuth();
  const { setCenter } = useReactFlow();
  
  const {
    nodes,
    edges,
    selectedNode,
    selectedNodes,
    selectedEdge,
    updateNode,
    updateEdge,
    deleteNode,
    deleteMultipleNodes,
    duplicateNode,
    deleteEdge,
    setSelectedEdge,
    setSelectedNode,
    modelType,
    globalTags,
    addGlobalTag,
    globalDomains,
    addGlobalDomain,
    isLoading,
    userTypes,
    showEntityEditModal,
    closeEntityEditModal,
    openEntityEditModal,
    pendingDeleteEntityId,
    clearPendingDeleteEntity,
    entityToolbarPending,
    clearEntityToolbarPending,
    renameSchema,
    diagramFilter,
    clearDiagramFilter,
  } = useFlowStore();

  const [nodeData, setNodeData] = useState(null);
  const [edgeData, setEdgeData] = useState(null);
  const [isTagsSectionExpanded, setIsTagsSectionExpanded] = useState(false);
  const [isDomainsSectionExpanded, setIsDomainsSectionExpanded] = useState(false);
  const [isFieldsSectionExpanded, setIsFieldsSectionExpanded] = useState(true);
  const [isRelationshipsSectionExpanded, setIsRelationshipsSectionExpanded] = useState(false);
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTagText, setNewTagText] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0].value);
  const [showNewDomainForm, setShowNewDomainForm] = useState(false);
  const [newDomainText, setNewDomainText] = useState('');
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null);
  const [dragOverFieldIndex, setDragOverFieldIndex] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showSaveDomainModal, setShowSaveDomainModal] = useState(false);
  const [saveDomainData, setSaveDomainData] = useState(null);
  const [showManageIndexesModal, setShowManageIndexesModal] = useState(false);
  const [showBusinessRulesModal, setShowBusinessRulesModal] = useState(false);
  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel } = useConfirm();
  const [filterTag, setFilterTag] = useState(null);
  const [filterDomain, setFilterDomain] = useState(null);

  // Auto-focus ref for new fields
  const newFieldInputRef = useRef(null);
  const [shouldFocusNewField, setShouldFocusNewField] = useState(false);

  const availableSchemas = Array.from(new Set(nodes.map(n => n.data?.schema).filter(Boolean))).sort();

  const handleRenameSchemaGlobal = (oldSchema) => {
    if (!oldSchema) return;
    const newSchema = window.prompt(`Rename schema "${oldSchema}" for all entities:`, oldSchema);
    if (newSchema !== null && newSchema.trim() !== '' && newSchema.trim() !== oldSchema) {
      const trimmedNew = newSchema.trim();
      renameSchema(oldSchema, trimmedNew);
      if (nodeData) {
        setNodeData(prev => prev ? { ...prev, schema: trimmedNew } : prev);
      }
      toast.success(`Schema "${oldSchema}" renamed to "${trimmedNew}" across all entities!`);
    }
  };

  useEffect(() => {
    if (selectedNode) {
      setNodeData({ ...selectedNode.data });
      setEdgeData(null);
    } else if (selectedEdge) {
      setEdgeData({ ...selectedEdge.data });
      setNodeData(null);
    } else {
      setNodeData(null);
      setEdgeData(null);
    }
  }, [selectedNode, selectedEdge]);

  useEffect(() => {
    if (shouldFocusNewField && newFieldInputRef.current) {
      newFieldInputRef.current.focus();
      newFieldInputRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      setShouldFocusNewField(false);
    }
  }, [nodeData?.fields, shouldFocusNewField]);

  const isEdgeLockedByPartition = useCallback((edge) => {
    if (!edge) return false;
    const targetNode = nodes.find(n => n.id === edge.target);
    if (!targetNode) return false;
    const partitioning = targetNode.data?.partitioning;
    if (!partitioning || !partitioning.type || partitioning.type === 'none') return false;
    const lockedCols = partitioning.type === 'hash'
      ? (partitioning.columns || [])
      : (partitioning.column ? [partitioning.column] : (partitioning.columns || []));

    const sourceNode = nodes.find(n => n.id === edge.source);
    const srcName = sourceNode?.data?.name || 'entity';
    const fkName = (edge.data?.fkAlias && String(edge.data.fkAlias).trim()) || `${srcName.toLowerCase()}_id`;

    return lockedCols.some(col => col.toLowerCase() === fkName.toLowerCase());
  }, [nodes]);

  const handleSaveNode = () => {
    if (selectedNode && nodeData) {
      updateNode(selectedNode.id, nodeData);
    }
  };

  const handleSaveEdge = () => {
    if (selectedEdge && edgeData) {
      if (isEdgeLockedByPartition(selectedEdge)) {
        const oldFkAlias = (selectedEdge.data?.fkAlias && String(selectedEdge.data.fkAlias).trim()) || '';
        const newFkAlias = (edgeData.fkAlias && String(edgeData.fkAlias).trim()) || '';
        if (oldFkAlias !== newFkAlias) {
          toast.error('The FK alias is locked because this Foreign Key is used in table partitioning. Remove or change the partition before renaming.');
          return;
        }
      }
      const payload = { ...edgeData };
      if (typeof payload.fkAlias === 'string') {
        payload.fkAlias = payload.fkAlias.trim();
      }
      updateEdge(selectedEdge.id, payload);
    }
  };

  const handleCardinalityChange = (newCardinality) => {
    logger.log('🎯 handleCardinalityChange called:', {
      selectedEdgeId: selectedEdge?.id,
      oldCardinality: edgeData?.cardinality,
      newCardinality,
      isLocked: edgeData?.cardinalityLocked
    });

    if (isEdgeLockedByPartition(selectedEdge)) {
      toast.error('Cardinality is locked because this Foreign Key is used in active partitioning. Remove the partition before changing cardinality.');
      return;
    }
    
    // Prevent changes if cardinality is locked
    if (edgeData?.cardinalityLocked) {
      logger.log('🔒 Cardinality is locked, cannot change');
      return;
    }
    
    if (selectedEdge && edgeData) {
      const newEdgeData = { ...edgeData, cardinality: newCardinality };
      setEdgeData(newEdgeData);
      
      logger.log('📤 Calling updateEdge with:', {
        edgeId: selectedEdge.id,
        newEdgeData
      });
      
      // Update immediately
      updateEdge(selectedEdge.id, newEdgeData);
    } else {
      logger.log('⚠️ Cannot update - missing edge or edgeData');
    }
  };

  const handleDeleteNode = useCallback(
    async (nodeOverride) => {
      const node = (nodeOverride && typeof nodeOverride.id === 'string') ? nodeOverride : selectedNode;
      if (!node) return;
      const nodeId = node.id;
      await confirm({
        title: 'Delete Entity',
        message: `Are you sure you want to delete "${node.data?.name || 'this entity'}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: () => {
          deleteNode(nodeId);
        },
      });
    },
    [selectedNode, confirm, deleteNode]
  );

  useEffect(() => {
    if (!pendingDeleteEntityId) return;
    const node = nodes.find((n) => n.id === pendingDeleteEntityId);
    clearPendingDeleteEntity();
    if (!node) return;
    handleDeleteNode(node);
  }, [pendingDeleteEntityId, nodes, clearPendingDeleteEntity, handleDeleteNode]);

  useEffect(() => {
    if (!entityToolbarPending) return;
    const { action, nodeId } = entityToolbarPending;
    if (selectedNode?.id !== nodeId) return;

    clearEntityToolbarPending();

    switch (action) {
      case 'duplicate':
        setShowDuplicateModal(true);
        break;
      case 'saveDomain':
        if (!userHasRole) {
          toast.error('You need an active subscription to save objects');
          break;
        }
        setSaveDomainData({
          entity: {
            ...selectedNode.data,
            type: selectedNode.type,
          },
        });
        setShowSaveDomainModal(true);
        break;
      case 'indexes':
        if (selectedNode.type !== 'text' && selectedNode.data?.fields) {
          setTimeout(() => setShowManageIndexesModal(true), 0);
        }
        break;
      case 'rules':
        setTimeout(() => setShowBusinessRulesModal(true), 0);
        break;
      default:
        break;
    }
  }, [entityToolbarPending, selectedNode, userHasRole, clearEntityToolbarPending]);

  const fullEntityEditModal =
    showEntityEditModal && selectedNode && nodeData ? (
      <EntityEditModal
        isOpen={showEntityEditModal}
        onClose={() => closeEntityEditModal()}
        selectedNode={selectedNode}
        initialData={nodeData}
        modelType={modelType}
        globalTags={globalTags}
        addGlobalTag={addGlobalTag}
        globalDomains={globalDomains}
        addGlobalDomain={addGlobalDomain}
        edges={edges}
        nodes={nodes}
        userTypes={userTypes}
        userHasRole={userHasRole}
        onSave={(data) => {
          updateNode(selectedNode.id, data);
          setNodeData(data);
        }}
        onSelectEdge={(edge) => {
          setSelectedEdge(edge);
          closeEntityEditModal();
        }}
      />
    ) : null;

  const handleDeleteMultipleNodes = async () => {
    if (selectedNodes && selectedNodes.length > 1) {
      const nodeIds = selectedNodes.map(n => n.id);
      const nodeNames = selectedNodes.map(n => n.data?.name || 'Unnamed').join(', ');
      
      await confirm({
        title: 'Delete Multiple Entities',
        message: `Are you sure you want to delete ${selectedNodes.length} entities?\n\nEntities: ${nodeNames}\n\nThis action cannot be undone and will also delete all relationships connected to these entities.`,
        confirmText: `Delete ${selectedNodes.length} Entities`,
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: () => {
          deleteMultipleNodes(nodeIds);
          toast.success(`${selectedNodes.length} entities deleted successfully!`);
        }
      });
    }
  };

  const handleDuplicateNode = () => {
    if (selectedNode) {
      setShowDuplicateModal(true);
    }
  };

  const handleConfirmDuplicate = (newName) => {
    if (selectedNode && newName) {
      duplicateNode(selectedNode.id, newName);
      setShowDuplicateModal(false);
      toast.success(`Entity "${newName}" created successfully!`);
    }
  };

  const handleSaveDomainEntity = () => {
    if (selectedNode) {
      setSaveDomainData({ 
        entity: {
          ...nodeData,
          type: selectedNode.type // Include the node type (table, view, fact, dimension, etc.)
        }
      });
      setShowSaveDomainModal(true);
    }
  };

  const handleSaveDomainField = (field) => {
    setSaveDomainData({ field });
    setShowSaveDomainModal(true);
  };

  const handleConfirmSaveDomain = async (domainData) => {
    try {
      await domainApi.create(domainData);
      toast.success('Object saved successfully!');
      setShowSaveDomainModal(false);
      setSaveDomainData(null);
    } catch (error) {
      logger.error('Error saving domain:', error);
      toast.error(error.message || 'Failed to save object');
    }
  };

  const handleSaveIndexes = (indexes) => {
    if (selectedNode && nodeData) {
      const updatedData = {
        ...nodeData,
        indexes: indexes
      };
      setNodeData(updatedData);
      updateNode(selectedNode.id, updatedData);
      toast.success('Indexes updated successfully!');
    }
  };

  const handleSaveBusinessRules = (businessRules) => {
    if (selectedNode && nodeData) {
      const updatedData = {
        ...nodeData,
        businessRules: businessRules
      };
      setNodeData(updatedData);
      updateNode(selectedNode.id, updatedData);
      toast.success('Business rules saved successfully!');
    }
  };

  const handleDeleteEdge = async () => {
    if (selectedEdge) {
      if (isEdgeLockedByPartition(selectedEdge)) {
        const targetNode = nodes.find(n => n.id === selectedEdge.target);
        const targetName = targetNode?.data?.name || 'target entity';
        const partType = targetNode?.data?.partitioning?.type?.toUpperCase() || 'PARTITION';
        await confirm({
          title: 'Foreign Key Locked by Partitioning',
          message: `This relationship defines a Foreign Key used as an active partition key in table "${targetName}" (${partType}).\n\nPlease remove or change the partitioning configuration in "${targetName}" before deleting this relationship.`,
          confirmText: 'OK',
          cancelText: null,
          type: 'warning',
          onConfirm: () => {}
        });
        return;
      }

      const edgeId = selectedEdge.id;
      await confirm({
        title: 'Delete Relationship',
        message: 'Are you sure you want to delete this relationship? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: () => {
          deleteEdge(edgeId);
          // useEffect will handle clearing the state when selectedEdge becomes null
        }
      });
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedFieldIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedFieldIndex !== index) {
      setDragOverFieldIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverFieldIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedFieldIndex === null || draggedFieldIndex === dropIndex) {
      setDraggedFieldIndex(null);
      setDragOverFieldIndex(null);
      return;
    }

    // Reorder fields array
    const newFields = [...nodeData.fields];
    const [draggedField] = newFields.splice(draggedFieldIndex, 1);
    newFields.splice(dropIndex, 0, draggedField);

    setNodeData({ ...nodeData, fields: newFields });
    setDraggedFieldIndex(null);
    setDragOverFieldIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedFieldIndex(null);
    setDragOverFieldIndex(null);
  };

  const addField = () => {
    if (nodeData) {
      setIsFieldsSectionExpanded(true);
      // Check if there's already a PK, if not, make the first field a PK
      const hasPrimaryKey = nodeData.fields.some(f => f.isPrimaryKey);
      const newField = createDefaultField('', 'VARCHAR', !hasPrimaryKey && nodeData.fields.length === 0);
      
      setNodeData({
        ...nodeData,
        fields: [...nodeData.fields, newField],
      });
      setShouldFocusNewField(true);
    }
  };

  // Helper function to check if a node has active relationships
  const hasActiveRelationships = (nodeId) => {
    if (!edges || !nodeId) return false;
    return edges.some(edge => edge.source === nodeId || edge.target === nodeId);
  };

  const partitioning = nodeData?.partitioning;
  const isPartitionActive = partitioning && partitioning.type && partitioning.type !== 'none';
  const partitionLockedColumnNames = isPartitionActive
    ? (partitioning.type === 'hash'
        ? (partitioning.columns || [])
        : (partitioning.column ? [partitioning.column] : (partitioning.columns || [])))
    : [];

  const updateField = async (index, field) => {
    if (nodeData) {
      const oldField = nodeData.fields[index];

      // Block modification if field is locked by active partitioning
      if (oldField && partitionLockedColumnNames.includes(oldField.name)) {
        if (field.name !== oldField.name || field.type !== oldField.type) {
          toast.error(`Field "${oldField.name}" is locked by ${partitioning?.type?.toUpperCase()} partitioning. Remove or change the partition before modifying it.`);
          return;
        }
      }
      
      // Check if trying to unmark a PK when there are active relationships
      if (oldField.isPrimaryKey && !field.isPrimaryKey && selectedNode) {
        const hasRelationships = hasActiveRelationships(selectedNode.id);
        
        if (hasRelationships) {
          await confirm({
            title: 'Cannot Remove Primary Key',
            message: `This Primary Key is being used in active relationships.\n\nPlease delete all relationships connected to "${selectedNode.data?.name}" before removing the Primary Key.`,
            confirmText: 'OK',
            cancelText: null,
            type: 'warning',
            onConfirm: () => {}
          });
          return; // Don't update the field
        }
      }
      
      // Check if this is the last PK and trying to unmark it
      if (oldField.isPrimaryKey && !field.isPrimaryKey && selectedNode) {
        const pkCount = nodeData.fields.filter(f => f.isPrimaryKey).length;
        
        if (pkCount === 1 && hasActiveRelationships(selectedNode.id)) {
          await confirm({
            title: 'Cannot Remove Last Primary Key',
            message: `This is the only Primary Key in "${selectedNode.data?.name}" and it has active relationships.\n\nPlease delete all relationships before removing the Primary Key.`,
            confirmText: 'OK',
            cancelText: null,
            type: 'warning',
            onConfirm: () => {}
          });
          return; // Don't update the field
        }
      }
      
      const newFields = [...nodeData.fields];
      newFields[index] = field;
      setNodeData({ ...nodeData, fields: newFields });
    }
  };

  const deleteField = async (index) => {
    if (nodeData) {
      const field = nodeData.fields[index];

      // Prevent deletion of fields used in active partitioning
      if (field && partitionLockedColumnNames.includes(field.name)) {
        await confirm({
          title: 'Field Locked by Partitioning',
          message: `The field "${field.name}" is currently used as a partition key for this table (${partitioning?.type?.toUpperCase()}).\n\nTo delete or modify this field, please remove or change the partitioning configuration in the entity properties first.`,
          confirmText: 'OK',
          cancelText: null,
          type: 'warning',
          onConfirm: () => {}
        });
        return;
      }
      
      // Prevent deletion of primary key fields if they're being used in relationships
      if (field?.isPrimaryKey && selectedNode) {
        const hasRelationships = hasActiveRelationships(selectedNode.id);
        
        if (hasRelationships) {
          await confirm({
            title: 'Cannot Delete Primary Key',
            message: `This Primary Key is being used in active relationships.\n\nPlease delete all relationships connected to "${selectedNode.data?.name}" before deleting the Primary Key.`,
            confirmText: 'OK',
            cancelText: null,
            type: 'warning',
            onConfirm: () => {}
          });
          return;
        }
        
        // Check if this is the last PK
        const pkCount = nodeData.fields.filter(f => f.isPrimaryKey).length;
        
        if (pkCount === 1) {
          await confirm({
            title: 'Delete Last Primary Key',
            message: `You are about to delete the only Primary Key in "${selectedNode.data?.name}".\n\nWithout a Primary Key, you will not be able to create relationships with this table.`,
            confirmText: 'Delete Anyway',
            cancelText: 'Cancel',
            type: 'warning',
            onConfirm: () => {
              // Continue with deletion
              const newFields = nodeData.fields.filter((_, i) => i !== index);
              setNodeData({ ...nodeData, fields: newFields });
            }
          });
          return;
        }
      }
      
      const fieldName = field?.name || 'this field';
      await confirm({
        title: 'Delete Field',
        message: `Are you sure you want to delete field "${fieldName}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: () => {
          const newFields = nodeData.fields.filter((_, i) => i !== index);
          setNodeData({ ...nodeData, fields: newFields });
        }
      });
    }
  };

  const getDataTypes = () => {
    const base = DATA_TYPES[modelType.toUpperCase()] || DATA_TYPES.RELATIONAL;
    const udtOptions = (userTypes || []).map(u => `UDT:${u.name}`);
    return udtOptions.length > 0 ? [...base, ...udtOptions] : base;
  };

  // Get relationships connected to the selected node
  const getNodeRelationships = () => {
    if (!selectedNode || !edges) return { outgoing: [], incoming: [] };

    const outgoing = edges
      .filter(edge => edge.source === selectedNode.id)
      .map(edge => {
        const targetNode = nodes.find(n => n.id === edge.target);
        return {
          ...edge,
          targetNode,
          direction: 'outgoing'
        };
      });

    const incoming = edges
      .filter(edge => edge.target === selectedNode.id)
      .map(edge => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        return {
          ...edge,
          sourceNode,
          direction: 'incoming'
        };
      });

    return { outgoing, incoming };
  };

  const handleRelationshipClick = (edge) => {
    setSelectedEdge(edge);
  };

  // Tag management functions
  const addTagToEntity = (tag) => {
    if (nodeData && selectedNode && !isLoading) {
      const entityTag = {
        id: Date.now().toString() + '_' + Math.random(),
        text: tag.text,
        color: tag.color,
        globalTagId: tag.id, // Reference to global tag
      };
      const updatedTags = [...(nodeData.tags || []), entityTag];
      const updatedColor = updatedTags.length === 1 ? entityTag.color : nodeData.color;
      const updatedData = {
        ...nodeData,
        tags: updatedTags,
        color: updatedColor
      };
      setNodeData(updatedData);
      // Auto-save immediately (only if not loading)
      updateNode(selectedNode.id, { tags: updatedTags, color: updatedColor });
    }
  };

  const createAndAddNewTag = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!newTagText.trim()) return;
    
    // Create global tag
    const globalTag = {
      id: Date.now().toString(),
      text: newTagText.trim(),
      color: newTagColor,
    };
    
    addGlobalTag(globalTag);
    addTagToEntity(globalTag);
    
    // Reset form
    setNewTagText('');
    setNewTagColor(TAG_COLORS[0].value);
    setShowNewTagForm(false);
  };

  const updateTag = (tagId, updates) => {
    if (nodeData && selectedNode && !isLoading) {
      const newTags = (nodeData.tags || []).map(tag =>
        tag.id === tagId ? { ...tag, ...updates } : tag
      );
      const newColor = newTags.length > 0 ? newTags[0].color : null;
      setNodeData({ 
        ...nodeData, 
        tags: newTags,
        color: newColor
      });
      // Auto-save immediately (only if not loading)
      updateNode(selectedNode.id, { tags: newTags, color: newColor });
    }
  };

  const deleteTag = (tagId) => {
    if (nodeData && selectedNode && !isLoading) {
      const newTags = (nodeData.tags || []).filter(tag => tag.id !== tagId);
      const newColor = newTags.length > 0 ? newTags[0].color : null;
      setNodeData({ 
        ...nodeData, 
        tags: newTags,
        color: newColor
      });
      // Auto-save immediately (only if not loading)
      updateNode(selectedNode.id, { tags: newTags, color: newColor });
    }
  };

  // Domain management functions
  const addDomainToEntity = (globalDomain) => {
    if (nodeData && selectedNode && !isLoading) {
      const alreadyAdded = (nodeData.domains || []).some(d => d.globalDomainId === globalDomain.id);
      if (alreadyAdded) return;
      const entityDomain = {
        id: Date.now().toString() + '_' + Math.random(),
        text: globalDomain.text,
        globalDomainId: globalDomain.id,
      };
      const updatedDomains = [...(nodeData.domains || []), entityDomain];
      setNodeData({ ...nodeData, domains: updatedDomains });
      updateNode(selectedNode.id, { domains: updatedDomains });
    }
  };

  const createAndAddNewDomain = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!newDomainText.trim()) return;
    const globalDomain = {
      id: Date.now().toString(),
      text: newDomainText.trim(),
    };
    addGlobalDomain(globalDomain);
    addDomainToEntity(globalDomain);
    setNewDomainText('');
    setShowNewDomainForm(false);
  };

  const removeDomainFromEntity = (domainId) => {
    if (nodeData && selectedNode && !isLoading) {
      const updatedDomains = (nodeData.domains || []).filter(d => d.id !== domainId);
      setNodeData({ ...nodeData, domains: updatedDomains });
      updateNode(selectedNode.id, { domains: updatedDomains });
    }
  };

  const moveTagUp = (index) => {
    if (nodeData && selectedNode && !isLoading && index > 0) {
      const newTags = [...(nodeData.tags || [])];
      [newTags[index - 1], newTags[index]] = [newTags[index], newTags[index - 1]];
      const newColor = newTags.length > 0 ? newTags[0].color : null;
      setNodeData({ ...nodeData, tags: newTags, color: newColor });
      // Auto-save immediately (only if not loading)
      updateNode(selectedNode.id, { tags: newTags, color: newColor });
    }
  };

  const moveTagDown = (index) => {
    if (nodeData && selectedNode && !isLoading && index < (nodeData.tags || []).length - 1) {
      const newTags = [...(nodeData.tags || [])];
      [newTags[index], newTags[index + 1]] = [newTags[index + 1], newTags[index]];
      const newColor = newTags.length > 0 ? newTags[0].color : null;
      setNodeData({ ...nodeData, tags: newTags, color: newColor });
      // Auto-save immediately (only if not loading)
      updateNode(selectedNode.id, { tags: newTags, color: newColor });
    }
  };

  const propertiesModals = (
    <>
      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
        onCancel={cancel}
      />

      {fullEntityEditModal}

      <DuplicateEntityModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onConfirm={handleConfirmDuplicate}
        originalName={selectedNode?.data?.name || ''}
      />

      {showSaveDomainModal && (
        <SaveDomainModal
          onClose={() => {
            setShowSaveDomainModal(false);
            setSaveDomainData(null);
          }}
          onSave={handleConfirmSaveDomain}
          entityData={saveDomainData?.entity}
          fieldData={saveDomainData?.field}
          modelType={modelType}
        />
      )}

      {showManageIndexesModal && selectedNode && nodeData && (() => {
        const incomingEdges = edges.filter(edge => edge.target === selectedNode.id);
        const virtualFKFields = incomingEdges
          .filter(edge => {
            const cardinality = edge.data?.cardinality;
            return cardinality !== 'N:N';
          })
          .map(edge => {
            const sourceNode = nodes.find(n => n.id === edge.source);
            const sourceName = sourceNode?.data?.name || 'unknown';

            return {
              sourceName: sourceName,
              isForeignKey: true,
              isVirtual: true,
              edgeId: edge.id,
              description: `Foreign key to ${sourceName}`
            };
          });

        return (
          <ManageIndexesModal
            isOpen={showManageIndexesModal}
            onClose={() => setShowManageIndexesModal(false)}
            entityName={nodeData.name}
            fields={nodeData.fields || []}
            virtualFKFields={virtualFKFields}
            indexes={nodeData.indexes || []}
            onSave={handleSaveIndexes}
          />
        );
      })()}

      {showBusinessRulesModal && selectedNode && nodeData && (
        <BusinessRulesModal
          isOpen={showBusinessRulesModal}
          onClose={() => setShowBusinessRulesModal(false)}
          entity={selectedNode}
          onSave={handleSaveBusinessRules}
        />
      )}
    </>
  );

  // Only hide panel if explicitly closed via toggle, not when selection is cleared.
  // Keep dialogs/modals mounted so node toolbar actions work when the panel is hidden.
  if (!isOpen) {
    return propertiesModals;
  }

  // Show entity list when nothing is selected
  if (!selectedNode && !selectedEdge && selectedNodes.length === 0) {
    const entityNodes = nodes
      .filter(n => n.type !== NODE_TYPES.TEXT)
      .sort((a, b) => (a.data?.name || '').localeCompare(b.data?.name || ''));

    const filteredEntities = entityNodes.filter(n => {
      const diagramMatch = isNodeMatchingFilter(n, diagramFilter);
      const tagMatch = filterTag ? (n.data?.tags || []).some(t => (typeof t === 'string' ? t : t.text) === filterTag) : true;
      const domainMatch = filterDomain ? (n.data?.domains || []).some(d => (typeof d === 'string' ? d : d.text) === filterDomain) : true;
      return diagramMatch && tagMatch && domainMatch;
    });

    const filterTagColor = filterTag
      ? entityNodes.flatMap(n => n.data?.tags || []).find(t => (typeof t === 'string' ? t : t.text) === filterTag)?.color
      : null;

    const handleTagFilter = (tagText) => {
      setFilterTag(prev => (prev === tagText ? null : tagText));
    };

    const handleDomainFilter = (domainText) => {
      setFilterDomain(prev => (prev === domainText ? null : domainText));
    };

    return (
      <div className="fixed lg:relative right-0 top-0 h-full w-[365px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl lg:shadow-none z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Entities</h2>
            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full font-medium">
              {filteredEntities.length}/{entityNodes.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Active filters */}
        {(filterTag || filterDomain || diagramFilter?.active) && (
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center flex-wrap gap-2 bg-blue-50 dark:bg-blue-900/20 flex-shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Filtered by:</span>
            {diagramFilter?.active && (
              <button
                onClick={clearDiagramFilter}
                className="flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full bg-blue-600 text-white font-medium hover:opacity-80 transition-opacity"
                title="Diagram filter active. Click to clear diagram filter"
              >
                <Filter size={9} />
                <span>Diagram Filter</span>
                <X size={9} />
              </button>
            )}
            {filterTag && (
              <button
                onClick={() => setFilterTag(null)}
                className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full text-white font-medium hover:opacity-80 transition-opacity"
                style={{ backgroundColor: filterTagColor || '#6B7280' }}
                title="Click to remove tag filter"
              >
                <Tag size={9} />
                {filterTag}
                <X size={9} />
              </button>
            )}
            {filterDomain && (
              <button
                onClick={() => setFilterDomain(null)}
                className="flex items-center gap-1 px-2 py-0.5 text-xs italic rounded-full bg-gray-500 dark:bg-gray-500 text-white font-medium hover:opacity-80 transition-opacity"
                title="Click to remove domain filter"
              >
                {filterDomain}
                <X size={9} />
              </button>
            )}
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 font-medium">
              {filteredEntities.length}/{entityNodes.length}
            </span>
          </div>
        )}

        {/* Entity list */}
        <div className="flex-1 overflow-y-auto">
          {filteredEntities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 gap-3">
              <p className="text-gray-400 text-sm text-center">
                {diagramFilter?.active
                  ? 'No entities match the current diagram filter'
                  : filterTag && filterDomain
                  ? `No entities with tag "${filterTag}" and domain "${filterDomain}"`
                  : filterTag
                  ? `No entities with tag "${filterTag}"`
                  : filterDomain
                  ? `No entities with domain "${filterDomain}"`
                  : 'No entities in this model'}
              </p>
              {(filterTag || filterDomain || diagramFilter?.active) && (
                <button
                  onClick={() => { setFilterTag(null); setFilterDomain(null); clearDiagramFilter(); }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {filteredEntities.map(node => (
                <div
                  key={node.id}
                  className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                >
                  {/* Entity name row */}
                  <button
                    onClick={() => {
                      setSelectedNode(node);
                      setCenter(node.position.x + 125, node.position.y + 75, { zoom: 1.5, duration: 800 });
                    }}
                    className="w-full text-left flex items-center gap-2 group"
                  >
                    <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 capitalize font-mono leading-tight">
                      {node.type}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                      {node.data?.name || 'Unnamed'}
                    </span>
                  </button>

                  {/* Tags row */}
                  {(node.data?.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5 pl-0">
                      {(node.data?.tags || []).map(tag => (
                        <button
                          key={tag.id}
                          onClick={() => handleTagFilter(tag.text)}
                          className={`px-2 py-0.5 text-[11px] rounded-full text-white font-medium transition-all hover:scale-105 active:scale-95 ${
                            filterTag === tag.text
                              ? 'ring-2 ring-offset-1 ring-gray-800 dark:ring-white'
                              : ''
                          }`}
                          style={{ backgroundColor: tag.color }}
                          title={filterTag === tag.text ? 'Click to remove filter' : `Filter by "${tag.text}"`}
                        >
                          {tag.text}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Domains row */}
                  {(node.data?.domains || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1 pl-0">
                      {(node.data?.domains || []).map(domain => (
                        <button
                          key={domain.id}
                          onClick={() => handleDomainFilter(domain.text)}
                          className={`px-2 py-0.5 text-[11px] italic rounded-full transition-all hover:scale-105 active:scale-95 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 ${
                            filterDomain === domain.text
                              ? 'ring-2 ring-offset-1 ring-gray-500 dark:ring-gray-300'
                              : ''
                          }`}
                          title={filterDomain === domain.text ? 'Click to remove filter' : `Filter by "${domain.text}"`}
                        >
                          {domain.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show multiple selection state
  if (selectedNodes.length > 1) {
    return (
      <>
        {/* Overlay for mobile */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />

        {/* Properties Panel - Multiple Selection */}
        <div className="fixed lg:relative right-0 top-0 h-full w-[365px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl lg:shadow-none z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Multiple Selection
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-blue-600 dark:text-blue-400 font-semibold text-2xl">
                  {selectedNodes.length}
                </div>
                <div className="text-blue-900 dark:text-blue-100">
                  entities selected
                </div>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                You can drag all selected entities together to move them.
              </p>
            </div>

            {/* List of selected entities */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Selected Entities:
              </h3>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {selectedNodes.map((node) => (
                  <div
                    key={node.id}
                    className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600 text-sm"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {node.data?.name || 'Unnamed'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {node.type} • {node.data?.fields?.length || 0} field{node.data?.fields?.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> Editing is disabled when multiple entities are selected. 
                Select a single entity to edit its properties.
              </p>
            </div>
          </div>

          {/* Footer - Delete Button Only */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={handleDeleteMultipleNodes}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 size={16} />
              <span>Delete {selectedNodes.length} Entities</span>
            </button>
          </div>
        </div>

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmOpen}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText={confirmConfig.cancelText}
          type={confirmConfig.type}
          onConfirm={confirmConfig.onConfirm}
          onCancel={cancel}
        />
      </>
    );
  }

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Properties Panel */}
      <div className="fixed lg:relative right-0 top-0 h-full w-[365px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl lg:shadow-none z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Properties
          </h2>
          <div className="flex items-center gap-1">
            {selectedNode && (
              <button
                onClick={() => openEntityEditModal()}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 dark:text-gray-400 transition-colors"
                title="Edit in full modal"
              >
                <Maximize2 size={17} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 flex flex-col p-4 min-h-0 overflow-hidden">
          {selectedNode && nodeData && (
            <div className="flex flex-col gap-4 h-full min-h-0">
              {/* Top Section - Fixed Size */}
              <div className="flex-shrink-0 space-y-4">
              {/* Schema */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Schema (optional)
                  </label>
                  {nodeData.schema && (
                    <button
                      type="button"
                      onClick={() => handleRenameSchemaGlobal(nodeData.schema)}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline font-normal"
                      title="Rename schema across all entities"
                    >
                      Rename globally
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  list="existing-schemas-sidebar"
                  value={nodeData.schema || ''}
                  onChange={(e) =>
                    setNodeData({ ...nodeData, schema: e.target.value })
                  }
                  placeholder="e.g. public, sales, dbo"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <datalist id="existing-schemas-sidebar">
                  {availableSchemas.map((sch) => (
                    <option key={sch} value={sch} />
                  ))}
                </datalist>
              </div>

              {/* Entity Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Entity Name
                </label>
                <input
                  type="text"
                  value={nodeData.name || ''}
                  onChange={(e) =>
                    setNodeData({ ...nodeData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={nodeData.description || ''}
                  onChange={(e) =>
                    setNodeData({ ...nodeData, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
              </div>

              {/* Materialized View Checkbox - Only for Views */}
              {selectedNode.type === NODE_TYPES.VIEW && (
                <div className="flex items-center gap-2 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                  <input
                    type="checkbox"
                    id="materialized-checkbox"
                    checked={nodeData.materialized || false}
                    onChange={(e) =>
                      setNodeData({ ...nodeData, materialized: e.target.checked })
                    }
                    className="rounded text-cyan-600 focus:ring-cyan-500"
                  />
                  <label 
                    htmlFor="materialized-checkbox"
                    className="text-sm font-medium text-cyan-900 dark:text-cyan-100 cursor-pointer flex items-center gap-1"
                  >
                    <Database size={14} />
                    Materialized View
                  </label>
                  <span className="text-xs text-cyan-700 dark:text-cyan-300 ml-auto">
                    {nodeData.materialized ? '✓ Materialized' : 'Standard'}
                  </span>
                </div>
              )}

              {/* Tags - Not for Text nodes */}
              {selectedNode.type !== NODE_TYPES.TEXT && (
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                  {/* Accordion Header */}
                  <button
                    type="button"
                    onClick={() => setIsTagsSectionExpanded(!isTagsSectionExpanded)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRight
                        size={16}
                        className={`transition-transform text-gray-700 dark:text-gray-200 ${isTagsSectionExpanded ? 'rotate-90' : ''}`}
                      />
                      <Tag size={14} className="text-gray-700 dark:text-gray-200" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Tags
                      </span>
                      {(nodeData.tags || []).length > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                          {(nodeData.tags || []).length}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Accordion Content */}
                  {isTagsSectionExpanded && (
                    <div className="p-3 pt-0 space-y-3">
                      {/* Entity Tags */}
                      {(nodeData.tags || []).length > 0 && (
                        <div className="space-y-2">
                      {(nodeData.tags || []).map((tag, index) => (
                        <div
                          key={tag.id}
                          className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex items-start gap-2 mb-2">
                            {/* Reorder buttons */}
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  moveTagUp(index);
                                }}
                                disabled={index === 0}
                                className="p-0.5 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move Up"
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  moveTagDown(index);
                                }}
                                disabled={index === (nodeData.tags || []).length - 1}
                                className="p-0.5 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move Down"
                              >
                                <ChevronDown size={14} />
                              </button>
                            </div>

                            {/* Tag input */}
                            <input
                              type="text"
                              value={tag.text || ''}
                              onChange={(e) => updateTag(tag.id, { text: e.target.value })}
                              placeholder="Tag text..."
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />

                            {/* Delete button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                deleteTag(tag.id);
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              title="Delete Tag"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          {/* Color picker */}
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-xs text-gray-600 dark:text-gray-400 mr-1">Color:</span>
                            <div className="flex flex-wrap gap-1">
                              {TAG_COLORS.map((color) => (
                                <button
                                  key={color.value}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    updateTag(tag.id, { color: color.value });
                                  }}
                                  className={`w-6 h-6 rounded border-2 transition-all ${
                                    tag.color === color.value
                                      ? 'border-gray-900 dark:border-white scale-110'
                                      : 'border-transparent hover:scale-105'
                                  }`}
                                  style={{ backgroundColor: color.value }}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </div>

                          {/* First tag indicator */}
                          {index === 0 && (
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              ★ This tag color is applied to the entity header
                            </div>
                          )}
                          </div>
                        ))}
                        </div>
                      )}

                      {/* Divider */}
                      {(nodeData.tags || []).length > 0 && (globalTags.length > 0 || showNewTagForm) && (
                        <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                      )}

                      {/* Available Tags from Library */}
                      {globalTags.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">
                            Available Tags:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {globalTags.map((tag) => (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  addTagToEntity(tag);
                                }}
                                className="px-2 py-1 text-xs rounded-md transition-all hover:scale-105 flex items-center gap-1"
                                style={{
                                  backgroundColor: tag.color,
                                  color: 'white',
                                }}
                                title="Click to add to entity"
                              >
                                <Plus size={12} />
                                {tag.text}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* New Tag Form */}
                      {showNewTagForm ? (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-2">
                          <input
                            type="text"
                            value={newTagText}
                            onChange={(e) => setNewTagText(e.target.value)}
                            placeholder="Tag text..."
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            autoFocus
                          />
                          <div className="flex flex-wrap gap-1 mb-2">
                            {TAG_COLORS.map((color) => (
                              <button
                                key={color.value}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setNewTagColor(color.value);
                                }}
                                className={`w-6 h-6 rounded border-2 transition-all ${
                                  newTagColor === color.value
                                    ? 'border-gray-900 dark:border-white scale-110'
                                    : 'border-transparent hover:scale-105'
                                }`}
                                style={{ backgroundColor: color.value }}
                                title={color.name}
                              />
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={createAndAddNewTag}
                              disabled={!newTagText.trim()}
                              className="flex-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Create & Add
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowNewTagForm(false);
                                setNewTagText('');
                                setNewTagColor(TAG_COLORS[0].value);
                              }}
                              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowNewTagForm(true);
                          }}
                          className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus size={16} />
                          Create New Tag
                        </button>
                      )}

                      {(nodeData.tags || []).length === 0 && globalTags.length === 0 && !showNewTagForm && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-2">
                          No tags yet. Create your first tag above.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Domain — Not for Text nodes */}
              {selectedNode.type !== NODE_TYPES.TEXT && (
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                  {/* Accordion Header */}
                  <button
                    type="button"
                    onClick={() => setIsDomainsSectionExpanded(!isDomainsSectionExpanded)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRight
                        size={16}
                        className={`transition-transform text-gray-700 dark:text-gray-200 ${isDomainsSectionExpanded ? 'rotate-90' : ''}`}
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Domain
                      </span>
                      {(nodeData.domains || []).length > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full">
                          {(nodeData.domains || []).length}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Accordion Content */}
                  {isDomainsSectionExpanded && (
                    <div className="p-3 pt-0 space-y-3">
                      {/* Current entity domains */}
                      {(nodeData.domains || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {(nodeData.domains || []).map((domain) => (
                            <span
                              key={domain.id}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-md text-xs"
                            >
                              <span className="italic">{domain.text}</span>
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeDomainFromEntity(domain.id); }}
                                className="text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-200 ml-0.5"
                                title="Remove domain"
                              >
                                <X size={11} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Available Domains from Library */}
                      {globalDomains.filter(gd => !(nodeData.domains || []).some(d => d.globalDomainId === gd.id)).length > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">
                            Available Domains:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {globalDomains
                              .filter(gd => !(nodeData.domains || []).some(d => d.globalDomainId === gd.id))
                              .map((gd) => (
                                <button
                                  key={gd.id}
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); addDomainToEntity(gd); }}
                                  className="px-2 py-1 text-xs rounded-md border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors flex items-center gap-1"
                                  title="Click to add to entity"
                                >
                                  <Plus size={11} />
                                  {gd.text}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* New Domain Form */}
                      {showNewDomainForm ? (
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg space-y-2">
                          <input
                            type="text"
                            value={newDomainText}
                            onChange={(e) => setNewDomainText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createAndAddNewDomain(e); } }}
                            placeholder="Domain name..."
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={createAndAddNewDomain}
                              disabled={!newDomainText.trim()}
                              className="flex-1 px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Create & Add
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowNewDomainForm(false); setNewDomainText(''); }}
                              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowNewDomainForm(true); }}
                          className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus size={16} />
                          Create New Domain
                        </button>
                      )}

                      {(nodeData.domains || []).length === 0 && globalDomains.length === 0 && !showNewDomainForm && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-2">
                          No domains yet. Create your first domain above.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* SQL Definition for Views */}
              {selectedNode.type === NODE_TYPES.VIEW && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <Code size={14} />
                    SQL Definition
                  </label>
                  <textarea
                    value={nodeData.sqlDefinition || ''}
                    onChange={(e) =>
                      setNodeData({ ...nodeData, sqlDefinition: e.target.value })
                    }
                    rows={4}
                    placeholder="SELECT column1, column2&#10;FROM table1&#10;WHERE condition"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none font-mono text-xs"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Define the SQL SELECT statement for this view
                  </p>
                </div>
              )}

              {/* Markdown Content for Text Nodes */}
              {selectedNode.type === NODE_TYPES.TEXT && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Content (Markdown)
                    </label>
                    <textarea
                      value={nodeData.content || ''}
                      onChange={(e) =>
                        setNodeData({ ...nodeData, content: e.target.value })
                      }
                      rows={12}
                      placeholder="# Heading&#10;&#10;Write your notes here using **Markdown**!&#10;&#10;- Bullet points&#10;- **Bold** and *italic*&#10;- [Links](https://example.com)&#10;- `code`"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Supports Markdown formatting: headings, bold, italic, lists, links, code, tables, etc.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Background Color
                    </label>
                    <div className="flex gap-2">
                      {[
                        { color: '#FEF9C3', label: 'Yellow' },
                        { color: '#DBEAFE', label: 'Blue' },
                        { color: '#D1FAE5', label: 'Green' },
                        { color: '#FCE7F3', label: 'Pink' },
                        { color: '#E0E7FF', label: 'Indigo' },
                        { color: '#FEE2E2', label: 'Red' },
                        { color: '#F3F4F6', label: 'Gray' },
                      ].map(({ color, label }) => (
                        <button
                          key={color}
                          onClick={() => setNodeData({ ...nodeData, backgroundColor: color })}
                          className={`w-10 h-10 rounded border-2 transition-all ${
                            nodeData.backgroundColor === color
                              ? 'border-blue-600 scale-110'
                              : 'border-gray-300 hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          title={label}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Width (px)
                    </label>
                    <input
                      type="range"
                      min="200"
                      max="600"
                      step="50"
                      value={nodeData.width || 300}
                      onChange={(e) =>
                        setNodeData({ ...nodeData, width: parseInt(e.target.value) })
                      }
                      className="w-full"
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      {nodeData.width || 300}px
                    </div>
                  </div>
                </>
              )}
              </div>
              {/* End of Top Section */}

              {/* Fields Section - Flexible Size */}
              {selectedNode.type !== NODE_TYPES.TEXT && (
              <div className={`${isFieldsSectionExpanded ? 'flex-1' : 'flex-shrink-0'} flex flex-col border border-gray-200 dark:border-gray-600 rounded-lg min-h-0`}>
                {/* Accordion Header */}
                <button
                  type="button"
                  onClick={() => setIsFieldsSectionExpanded(!isFieldsSectionExpanded)}
                  className="flex-shrink-0 w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight
                      size={16}
                      className={`transition-transform text-gray-700 dark:text-gray-200 ${isFieldsSectionExpanded ? 'rotate-90' : ''}`}
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Fields
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                      {nodeData.fields?.length || 0}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addField();
                    }}
                    className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    title="Add Field"
                  >
                    <Plus size={16} />
                  </button>
                </button>

                {/* Accordion Content */}
                {isFieldsSectionExpanded && (
                <div className="flex-1 p-3 pt-0 space-y-2 overflow-y-auto min-h-0">
                  {nodeData.fields?.map((field, index) => {
                    const isPartitionLocked = partitionLockedColumnNames.includes(field.name);
                    return (
                      <div
                        key={index}
                        draggable={!isPartitionLocked}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`p-3 rounded-lg border bg-gray-50 dark:bg-gray-700/50 transition-all ${
                          draggedFieldIndex === index ? 'opacity-50' : ''
                        } ${
                          dragOverFieldIndex === index
                            ? 'border-blue-500 dark:border-blue-400 border-2'
                            : isPartitionLocked
                              ? 'border-blue-300 dark:border-blue-700/80 bg-blue-50/30 dark:bg-blue-950/20'
                              : 'border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        {/* Partition Lock Notice */}
                        {isPartitionLocked && (
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100/70 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-300 mb-2 font-medium">
                            <Lock size={12} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                            <span>Locked by partitioning ({partitioning?.type?.toUpperCase()})</span>
                          </div>
                        )}

                        <div className="flex items-start gap-2 mb-2">
                          <div 
                            className={`mt-1.5 text-gray-400 ${isPartitionLocked ? 'cursor-not-allowed opacity-40' : 'cursor-move hover:text-gray-600 dark:hover:text-gray-300'}`}
                            title={isPartitionLocked ? "Reorder disabled for locked partition field" : "Drag to reorder"}
                          >
                            <GripVertical size={16} />
                          </div>
                          <input
                            ref={index === (nodeData.fields || []).length - 1 && shouldFocusNewField ? newFieldInputRef : null}
                            type="text"
                            value={field.name || ''}
                            disabled={isPartitionLocked}
                            onChange={(e) =>
                              updateField(index, { ...field, name: e.target.value })
                            }
                            placeholder="Field name"
                            title={isPartitionLocked ? "Field locked: used as active partition key" : undefined}
                            className={`flex-1 px-2 py-1 text-sm border rounded transition-colors ${
                              isPartitionLocked
                                ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/30 text-blue-950 dark:text-blue-200 font-semibold cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                            }`}
                          />
                          {isPartitionLocked && (
                            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-semibold rounded flex-shrink-0" title="Partition Key Locked">
                              <Lock size={10} /> PART
                            </span>
                          )}
                        </div>

                        <select
                          value={field.type || ''}
                          disabled={isPartitionLocked}
                          onChange={(e) =>
                            updateField(index, { ...field, type: e.target.value })
                          }
                          title={isPartitionLocked ? "Data type locked: used as active partition key" : undefined}
                          className={`w-full px-2 py-1 text-sm border rounded mb-2 transition-colors ${
                            isPartitionLocked
                              ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/30 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                          }`}
                        >
                          {getDataTypes().map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>

                        <input
                          type="text"
                          value={field.description || ''}
                          onChange={(e) =>
                            updateField(index, { ...field, description: e.target.value })
                          }
                          placeholder="Field description (optional)"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
                        />

                        {/* CHECK Constraint */}
                        <input
                          type="text"
                          value={field.checkConstraint || ''}
                          onChange={(e) =>
                            updateField(index, { ...field, checkConstraint: e.target.value })
                          }
                          placeholder="CHECK Constraint (e.g. age >= 18 or status IN ('A','M'))"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono mb-2"
                        />

                        <div className="flex items-center justify-between text-xs text-gray-700 dark:text-gray-300">
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.isPrimaryKey || false}
                                onChange={(e) =>
                                  updateField(index, {
                                    ...field,
                                    isPrimaryKey: e.target.checked,
                                    // When marking as PK, automatically set as NOT NULL
                                    nullable: e.target.checked ? false : field.nullable,
                                  })
                                }
                                className="rounded"
                              />
                              <Key size={12} className="text-yellow-500" />
                              <span>PK</span>
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.autoIncrement || false}
                                onChange={(e) =>
                                  updateField(index, {
                                    ...field,
                                    autoIncrement: e.target.checked,
                                  })
                                }
                                className="rounded"
                              />
                              <Hash size={12} className="text-blue-500" />
                              <span>Auto</span>
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.nullable !== false}
                                onChange={(e) =>
                                  updateField(index, {
                                    ...field,
                                    nullable: e.target.checked,
                                  })
                                }
                                disabled={field.isPrimaryKey}
                                className="rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <span className={field.isPrimaryKey ? 'opacity-50' : ''}>Nullable</span>
                            </label>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleSaveDomainField(field)}
                              disabled={!userHasRole}
                              className={`p-1 rounded transition-colors ${
                                userHasRole
                                  ? 'text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                                  : 'text-gray-400 opacity-50 cursor-not-allowed'
                              }`}
                              title={
                                userHasRole 
                                  ? "Save field as object" 
                                  : "You need an active subscription to save objects"
                              }
                            >
                              <Library size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteField(index)}
                              disabled={isPartitionLocked}
                              className={`p-1 rounded transition-colors ${
                                isPartitionLocked
                                  ? 'text-gray-400 opacity-40 cursor-not-allowed'
                                  : 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                              }`}
                              title={isPartitionLocked ? "Cannot delete: field is locked by active partitioning" : "Delete field"}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
              )}

              {/* Relationships Section */}
              {selectedNode.type !== NODE_TYPES.TEXT && (() => {
                const { outgoing, incoming } = getNodeRelationships();
                const totalRelationships = outgoing.length + incoming.length;

                if (totalRelationships === 0) return null;

                return (
                  <div className="flex-shrink-0 flex flex-col border border-gray-200 dark:border-gray-600 rounded-lg">
                    {/* Accordion Header */}
                    <button
                      type="button"
                      onClick={() => setIsRelationshipsSectionExpanded(!isRelationshipsSectionExpanded)}
                      className="flex-shrink-0 w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronRight
                          size={16}
                          className={`transition-transform text-gray-700 dark:text-gray-200 ${isRelationshipsSectionExpanded ? 'rotate-90' : ''}`}
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Relationships
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                          {totalRelationships}
                        </span>
                      </div>
                    </button>

                    {/* Accordion Content */}
                    {isRelationshipsSectionExpanded && (
                      <div className="p-3 pt-0 space-y-3">
                        {/* Outgoing Relationships */}
                        {outgoing.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1 mb-2">
                              <ArrowRight size={14} className="text-blue-600 dark:text-blue-400" />
                              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                                Outgoing ({outgoing.length})
                              </span>
                            </div>
                            <div className="space-y-2">
                              {outgoing.map((rel) => (
                                <button
                                  key={rel.id}
                                  onClick={() => handleRelationshipClick(rel)}
                                  className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link2 size={12} className="text-blue-600 dark:text-blue-400" />
                                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                                      {rel.data?.label || 'Unnamed'}
                                    </span>
                                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                                      {rel.data?.cardinality || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                    <ArrowRight size={12} />
                                    <span className="truncate">
                                      {rel.targetNode?.data?.name || 'Unknown'}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Incoming Relationships */}
                        {incoming.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1 mb-2">
                              <ArrowLeft size={14} className="text-green-600 dark:text-green-400" />
                              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                                Incoming ({incoming.length})
                              </span>
                            </div>
                            <div className="space-y-2">
                              {incoming.map((rel) => {
                                const fkName = (rel.data?.fkAlias && String(rel.data.fkAlias).trim()) || `${rel.sourceNode?.data?.name?.toLowerCase()}_id`;
                                const isFkPartitionLocked = partitionLockedColumnNames.some(col => col.toLowerCase() === fkName.toLowerCase());
                                return (
                                  <button
                                    key={rel.id}
                                    onClick={() => handleRelationshipClick(rel)}
                                    className={`w-full p-2 rounded-lg border transition-all text-left group ${
                                      isFkPartitionLocked
                                        ? 'border-blue-300 dark:border-blue-700/80 bg-blue-50/30 dark:bg-blue-950/20'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-green-500 dark:hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <Link2 size={12} className="text-green-600 dark:text-green-400" />
                                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                                        {rel.data?.label || 'Unnamed'}
                                      </span>
                                      <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                                        {rel.data?.cardinality || 'N/A'}
                                      </span>
                                      {isFkPartitionLocked && (
                                        <span className="ml-auto flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-semibold rounded" title="This Foreign Key is locked as an active partition key">
                                          <Lock size={10} /> PART KEY
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                      <ArrowLeft size={12} />
                                      <span className="truncate">
                                        {rel.sourceNode?.data?.name || 'Unknown'}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {selectedEdge && edgeData && (() => {
            const isEdgeLocked = isEdgeLockedByPartition(selectedEdge);
            const edgeTargetNode = nodes.find(n => n.id === selectedEdge.target);
            const edgeTargetPartType = edgeTargetNode?.data?.partitioning?.type?.toUpperCase() || 'PARTITION';

            return (
            <div className="space-y-4">
              {/* Partition Lock Notice */}
              {isEdgeLocked && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300 font-medium">
                  <Lock size={15} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>This Foreign Key is locked as an active <strong>{edgeTargetPartType}</strong> partition key in <strong>{edgeTargetNode?.data?.name}</strong>. Remove or change the partition before renaming the FK alias, changing cardinality, or deleting this relationship.</span>
                </div>
              )}

              {/* Connected Entities */}
              {(() => {
                const sourceNode = nodes.find(n => n.id === selectedEdge.source);
                const targetNode = nodes.find(n => n.id === selectedEdge.target);
                const nodeTypeLabel = (type) => type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
                const navigateTo = (node) => {
                  setSelectedNode(node);
                  setCenter(node.position.x + 125, node.position.y + 75, { zoom: 1.5, duration: 800 });
                };
                return (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 tracking-wide mb-2">
                      Related Entities
                    </p>
                    <div className="flex items-center gap-2">
                      {sourceNode && (
                        <button
                          onClick={() => navigateTo(sourceNode)}
                          className="flex-1 flex flex-col items-start gap-1 p-2 rounded-lg bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm transition-all text-left min-w-0"
                          title={`Ir para ${sourceNode.data?.name}`}
                        >
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate w-full">
                            {sourceNode.data?.name}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded capitalize">
                            {nodeTypeLabel(sourceNode.type)}
                          </span>
                        </button>
                      )}
                      <ArrowRight size={16} className="text-blue-400 dark:text-blue-500 flex-shrink-0" />
                      {targetNode && (
                        <button
                          onClick={() => navigateTo(targetNode)}
                          className="flex-1 flex flex-col items-start gap-1 p-2 rounded-lg bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm transition-all text-left min-w-0"
                          title={`Ir para ${targetNode.data?.name}`}
                        >
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate w-full">
                            {targetNode.data?.name}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded capitalize">
                            {nodeTypeLabel(targetNode.type)}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Edge Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Relationship Label
                </label>
                <input
                  type="text"
                  value={edgeData.label || ''}
                  onChange={(e) =>
                    setEdgeData({ ...edgeData, label: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., has_many, belongs_to"
                />
              </div>

              {edgeData.cardinality !== 'N:N' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    FK field alias (optional)
                    {isEdgeLocked && (
                      <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                        (Locked by Partitioning)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={edgeData.fkAlias ?? ''}
                    disabled={isEdgeLocked}
                    onChange={(e) =>
                      setEdgeData({ ...edgeData, fkAlias: e.target.value })
                    }
                    title={isEdgeLocked ? "FK alias is locked: used as active partition key" : undefined}
                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-colors ${
                      isEdgeLocked
                        ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/30 text-blue-950 dark:text-blue-200 font-semibold cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                    placeholder="e.g. parent, owner"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Shown in black italic to the left of the source entity name on the target entity&apos;s FK rows
                  </p>
                </div>
              )}

              {/* Cardinality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cardinality
                  {(edgeData.cardinalityLocked || isEdgeLocked) && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">
                      {isEdgeLocked ? '(Locked by Partitioning)' : '(Locked - cannot be changed)'}
                    </span>
                  )}
                </label>
                
                {/* Locked warning message */}
                {(edgeData.cardinalityLocked || isEdgeLocked) && (
                  <div className="mb-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                    <p className="text-xs text-yellow-800 dark:text-yellow-300">
                      🔒 Cardinality is locked {isEdgeLocked ? 'because this Foreign Key is used in active partitioning' : 'after creation'}. To change it, {isEdgeLocked ? 'remove the partition first' : 'delete this relationship and create a new one with the desired cardinality'}.
                    </p>
                  </div>
                )}
                
                {/* Visual cardinality selector */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {CARDINALITY.map((card) => (
                    <button
                      key={card.value}
                      type="button"
                      onClick={() => handleCardinalityChange(card.value)}
                      disabled={edgeData.cardinalityLocked || isEdgeLocked}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        (edgeData.cardinalityLocked || isEdgeLocked)
                          ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                          : ''
                      } ${
                        edgeData.cardinality === card.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-2">
                          {/* Visual representation */}
                          {card.value === '1:1' && (
                            <svg width="60" height="20" viewBox="0 0 60 20">
                              <line x1="5" y1="10" x2="55" y2="10" stroke="currentColor" strokeWidth="2"/>
                              <line x1="5" y1="5" x2="5" y2="15" stroke="currentColor" strokeWidth="2"/>
                              <line x1="55" y1="5" x2="55" y2="15" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          )}
                          {card.value === '1:N' && (
                            <svg width="60" height="20" viewBox="0 0 60 20">
                              <line x1="5" y1="10" x2="55" y2="10" stroke="currentColor" strokeWidth="2"/>
                              <line x1="5" y1="5" x2="5" y2="15" stroke="currentColor" strokeWidth="2"/>
                              <path d="M 48,5 L 55,10 L 48,15" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          )}
                          {card.value === 'N:1' && (
                            <svg width="60" height="20" viewBox="0 0 60 20">
                              <line x1="5" y1="10" x2="55" y2="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M 12,5 L 5,10 L 12,15" fill="none" stroke="currentColor" strokeWidth="2"/>
                              <line x1="55" y1="5" x2="55" y2="15" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          )}
                          {card.value === 'N:N' && (
                            <svg width="60" height="20" viewBox="0 0 60 20">
                              <line x1="5" y1="10" x2="55" y2="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M 12,5 L 5,10 L 12,15" fill="none" stroke="currentColor" strokeWidth="2"/>
                              <path d="M 48,5 L 55,10 L 48,15" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          )}
                        </div>
                        <span className="text-xs font-medium">{card.value}</span>
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                  {edgeData.cardinality === '1:1' && '○ One to One - Each record relates to exactly one record'}
                  {edgeData.cardinality === '1:N' && '○ One to Many - One record relates to multiple records'}
                  {edgeData.cardinality === 'N:1' && '○ Many to One - Multiple records relate to one record'}
                  {edgeData.cardinality === 'N:N' && '○ Many to Many - Multiple records relate to multiple records'}
                </div>
              </div>

              {/* Junction Table - Only for N:N relationships */}
              {edgeData.cardinality === 'N:N' && edgeData.junctionTable && (
                <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden bg-purple-50 dark:bg-purple-900/20">
                  <div className="bg-purple-100 dark:bg-purple-900/40 p-3 border-b border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2">
                      <Database size={16} className="text-purple-700 dark:text-purple-300" />
                      <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                        Junction Table (Read-Only)
                      </h3>
                    </div>
                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                      Automatically created for N:N relationships
                    </p>
                  </div>
                  
                  <div className="p-3 space-y-3">
                    {/* Table Name */}
                    <div>
                      <label className="block text-xs font-medium text-purple-900 dark:text-purple-100 mb-1">
                        Table Name
                      </label>
                      <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 rounded text-sm text-gray-900 dark:text-white font-mono">
                        {edgeData.junctionTable.name}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-xs font-medium text-purple-900 dark:text-purple-100 mb-1">
                        Description
                      </label>
                      <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 rounded text-xs text-gray-700 dark:text-gray-300">
                        {edgeData.junctionTable.description}
                      </div>
                    </div>

                    {/* Fields */}
                    <div>
                      <label className="block text-xs font-medium text-purple-900 dark:text-purple-100 mb-2">
                        Fields ({edgeData.junctionTable.fields.length})
                      </label>
                      <div className="space-y-1.5">
                        {edgeData.junctionTable.fields.map((field, idx) => (
                          <div 
                            key={idx}
                            className="px-2 py-1.5 bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded text-xs flex items-center gap-2"
                          >
                            <span className="font-mono font-semibold text-purple-700 dark:text-purple-300 min-w-[100px]">
                              {field.name}
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {field.type}
                            </span>
                            {field.isPrimaryKey && (
                              <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-[10px] font-medium">
                                PK
                              </span>
                            )}
                            {field.isForeignKey && (
                              <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-[10px] font-medium">
                                FK
                              </span>
                            )}
                            {!field.nullable && (
                              <span className="text-red-500 text-[10px]">NOT NULL</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Indexes */}
                    {edgeData.junctionTable.indexes && edgeData.junctionTable.indexes.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-purple-900 dark:text-purple-100 mb-2">
                          Indexes ({edgeData.junctionTable.indexes.length})
                        </label>
                        <div className="space-y-1.5">
                          {edgeData.junctionTable.indexes.map((index, idx) => (
                            <div 
                              key={idx}
                              className="px-2 py-1.5 bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded text-xs"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono font-semibold text-purple-700 dark:text-purple-300">
                                  {index.name}
                                </span>
                                <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-[10px]">
                                  {index.type}
                                </span>
                              </div>
                              <div className="text-gray-600 dark:text-gray-400 text-[11px]">
                                Columns: {index.columns.map(c => `${c.fieldName} ${c.order}`).join(', ')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

              {/* Warning Note */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2">
                      <p className="text-[11px] text-yellow-800 dark:text-yellow-300">
                        ⚠️ <strong>Note:</strong> This junction table cannot be modified directly. 
                        It will be automatically removed if you delete this relationship.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
        </div>

        {/* Footer - Fixed Action Buttons */}
        {(selectedNode || selectedEdge) && (
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex gap-2">
              <button
                onClick={selectedNode ? handleSaveNode : handleSaveEdge}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title={selectedNode ? "Save Entity Changes" : "Save Relationship Changes"}
              >
                <Save size={16} />
              </button>
              {selectedNode && (
                <>
                  <button
                    onClick={handleDuplicateNode}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    title="Duplicate Entity"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={handleSaveDomainEntity}
                    disabled={!userHasRole}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      userHasRole
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-50'
                    }`}
                    title={
                      userHasRole 
                        ? "Save as Object" 
                        : "You need an active subscription to save objects"
                    }
                  >
                    <Library size={16} />
                  </button>
                  {selectedNode.type !== 'text' && nodeData?.fields && (
                    <button
                      onClick={() => setShowManageIndexesModal(true)}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors relative"
                      title={`Manage Indexes (${nodeData?.indexes?.length || 0} indexes)`}
                    >
                      <Database size={16} />
                      {nodeData?.indexes && nodeData.indexes.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {nodeData.indexes.length}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setShowBusinessRulesModal(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors relative"
                    title="Business Rules"
                  >
                    <FileText size={16} />
                    {(nodeData?.businessRules?.entity || Object.keys(nodeData?.businessRules?.fields || {}).length > 0) && (
                      <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        ✓
                      </span>
                    )}
                  </button>
                </>
              )}
              <button
                onClick={selectedNode ? () => handleDeleteNode() : handleDeleteEdge}
                disabled={!selectedNode && selectedEdge && isEdgeLockedByPartition(selectedEdge)}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  !selectedNode && selectedEdge && isEdgeLockedByPartition(selectedEdge)
                    ? 'bg-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                title={
                  selectedNode
                    ? "Delete Entity"
                    : (selectedEdge && isEdgeLockedByPartition(selectedEdge))
                      ? "Cannot delete: Foreign Key is locked by active partitioning"
                      : "Delete Relationship"
                }
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {propertiesModals}
    </>
  );
};

export default Properties;
